/** Optional design interests. Budget is a customer's preference, never a quote. */
export const CUSTOM_WORLDS = [
  {
    id: 'storybook',
    title: 'Once upon a room',
    detail: 'Four castle alcoves, an upper gallery, a turret made for reading.',
    image: '/media/bliss-custom/storybook-castle.jpg',
    alt: 'Pearl-white castle with four arched sleeping alcoves, a supported upper gallery, brass stair rails, and a reading turret',
  },
  {
    id: 'woodland',
    title: 'Woodland hideaway',
    detail: 'A treehouse cabin, a sheltered sleeping den, a quiet reading perch.',
    image: '/media/bliss-custom/woodland-treehouse.jpg',
    alt: 'Timber treehouse bunk cabin with a pitched roof, left-side staircase, lower sleeping den, and reading bench',
  },
  {
    id: 'voyage',
    title: 'A grand voyage',
    detail: 'A curved ship’s hull, a captain’s sleeping cabin, hidden drawers.',
    image: '/media/bliss-custom/voyage-ship.jpg',
    alt: 'Ship-shaped lower berth beneath an offset captain’s cabin, with a brass porthole and right-side stairs',
  },
] as const

export const FEATURED_CUSTOM_WORLD = CUSTOM_WORLDS.find(world => world.id === 'storybook')!

// Acknowledgment records awareness of the current expectation, not an order or quote.
export const CUSTOM_INVESTMENT_NOTICE_VERSION = 'custom-creations-investment-v1'
export const CUSTOM_INVESTMENT_NOTICE =
  'Fully themed Bliss Custom Creations often require budgets in the high five figures to low six figures. Your investment depends on the design scope, materials, finishes, and installation. A tailored proposal will confirm your project’s price.'

export const CUSTOM_FEATURES = [
  { id: 'built_in', label: 'Built-in bunks' },
  { id: 'enclosures', label: 'Sleeping alcoves & enclosures (Bliss Custom)' },
  { id: 'wall_design', label: 'Shiplap & feature walls (Bliss Custom)' },
  { id: 'false_walls', label: 'False walls & room alterations (Bliss Custom)' },
  { id: 'stairs', label: 'Custom staircases' },
  { id: 'storage', label: 'Drawers, cubbies & hidden storage' },
  { id: 'tower_posts', label: 'Tower posts' },
  { id: 'lighting', label: 'Integrated LED lighting' },
  { id: 'finishes', label: 'Custom paint shades & wood stains' },
] as const

export type CustomWorld = (typeof CUSTOM_WORLDS)[number]['id']
export type CustomFeature = (typeof CUSTOM_FEATURES)[number]['id']
export type BlissCustomDiscovery = {
  worlds: CustomWorld[]
  features: CustomFeature[]
  scope: 'whole_room' | 'selected_details' | null
  comfortableBudget: number | null
  idea: string
  investmentAcknowledgment: typeof CUSTOM_INVESTMENT_NOTICE_VERSION | null
}

export function normalizeCustomDiscovery(value: unknown): BlissCustomDiscovery {
  const v = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return {
    worlds: CUSTOM_WORLDS.filter(w => Array.isArray(v.worlds) && v.worlds.includes(w.id)).map(
      w => w.id
    ),
    features: CUSTOM_FEATURES.filter(
      f => Array.isArray(v.features) && v.features.includes(f.id)
    ).map(f => f.id),
    scope: v.scope === 'whole_room' || v.scope === 'selected_details' ? v.scope : null,
    comfortableBudget:
      typeof v.comfortableBudget === 'number' &&
      Number.isFinite(v.comfortableBudget) &&
      v.comfortableBudget > 0 &&
      v.comfortableBudget <= 10000000
        ? Math.max(1, Math.round(v.comfortableBudget))
        : null,
    idea: typeof v.idea === 'string' ? v.idea.slice(0, 1500) : '',
    investmentAcknowledgment:
      v.investmentAcknowledgment === CUSTOM_INVESTMENT_NOTICE_VERSION
        ? CUSTOM_INVESTMENT_NOTICE_VERSION
        : null,
  }
}

export function customDiscoverySummary(value: unknown): string[] {
  const d = normalizeCustomDiscovery(value)
  const lines: string[] = []
  if (d.worlds.length)
    lines.push(
      `Design inspiration: ${CUSTOM_WORLDS.filter(w => d.worlds.includes(w.id))
        .map(w => w.title)
        .join(', ')}`
    )
  if (d.features.length)
    lines.push(
      `Custom interests (not ordered items): ${CUSTOM_FEATURES.filter(f =>
        d.features.includes(f.id)
      )
        .map(f => f.label)
        .join(', ')}`
    )
  if (d.scope)
    lines.push(
      `Preferred scope: ${d.scope === 'selected_details' ? 'a few custom details; explore a simpler design' : 'a whole-room custom experience'}`
    )
  if (d.comfortableBudget)
    lines.push(
      `Customer's comfortable budget: $${d.comfortableBudget.toLocaleString('en-US')} (preference, not an estimate)`
    )
  if (d.idea.trim()) lines.push(`Their idea: ${d.idea.trim()}`)
  if (d.investmentAcknowledgment)
    lines.push(
      'Investment expectation acknowledged: fully themed Custom Creations often require high-five-figure to low-six-figure budgets; individual proposal required.'
    )
  return lines
}

export const CUSTOM_DISCOVERY_SESSION_KEY = 'bliss_custom_discovery_draft'
export function readCustomDiscoveryDraft(
  planKey: string,
  fallback?: unknown
): BlissCustomDiscovery {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CUSTOM_DISCOVERY_SESSION_KEY) || 'null')
    if (saved?.planKey === planKey) return normalizeCustomDiscovery(saved.discovery)
  } catch {
    /* Optional session draft; saved plan remains available. */
  }
  return normalizeCustomDiscovery(fallback)
}
export function writeCustomDiscoveryDraft(planKey: string, discovery: BlissCustomDiscovery) {
  try {
    sessionStorage.setItem(CUSTOM_DISCOVERY_SESSION_KEY, JSON.stringify({ planKey, discovery }))
  } catch {
    /* keep in memory */
  }
}
